// src/services/paymentApi.js
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Function to handle common fetch logic and error handling
async function handleResponse(response) {
    const contentType = response.headers.get('content-type');
    let data;
    if (contentType && contentType.includes('application/json')) {
        data = await response.json();
    } else {
        data = { error: `Unexpected response type: ${contentType}`, responseText: await response.text() };
    }

    if (!response.ok) {
        const error = new Error(data.error || data.message || `HTTP error! status: ${response.status}`);
        error.status = response.status;
        error.data = data;
        throw error;
    }
    return data;
}

// Create Razorpay order
export const createRazorpayOrder = async (token, amount, purchaseType, meta = {}) => {
    const response = await fetch(`${API_URL}/payment_integration/create-order`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            amount: amount,
            purchase_type: purchaseType,
            meta: meta
        })
    });
    return handleResponse(response);
};

// Verify Razorpay payment
export const verifyRazorpayPayment = async (token, razorpayOrderId, razorpayPaymentId, razorpaySignature) => {
    const response = await fetch(`${API_URL}/payment_integration/verify-payment`, {
        method: 'POST',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            razorpay_order_id: razorpayOrderId,
            razorpay_payment_id: razorpayPaymentId,
            razorpay_signature: razorpaySignature
        })
    });
    return handleResponse(response);
};

// Load Razorpay script dynamically
export const loadRazorpayScript = () => {
    return new Promise((resolve) => {
        if (window.Razorpay) {
            resolve(true);
            return;
        }

        const script = document.createElement('script');
        script.src = 'https://checkout.razorpay.com/v1/checkout.js';
        script.onload = () => resolve(true);
        script.onerror = () => resolve(false);
        document.body.appendChild(script);
    });
};

// Initialize Razorpay payment
export const initializeRazorpayPayment = async (orderData, onSuccess, onError) => {
    const isLoaded = await loadRazorpayScript();
    if (!isLoaded) {
        onError('Failed to load Razorpay script');
        return;
    }

    const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: orderData.amount * 100, // Convert to paise
        currency: 'INR',
        order_id: orderData.id,
        name: 'Ecom Buddha',
        description: 'Credit Purchase',
        image: '/favicon.png',
        handler: function (response) {
            onSuccess(response);
        },
        prefill: {
            name: 'User',
            email: 'user@example.com',
            contact: '9999999999'
        },
        notes: {
            address: 'Ecom Buddha Office'
        },
        theme: {
            color: '#3399cc'
        },
        // Enable specific payment methods
        method: {
            netbanking: true,
            wallet: true,
            upi: true,
            card: true,
            emi: false,
            paylater: false
        },
        // UPI configuration for app redirects
        upi: {
            flow: 'collect', // 'collect' for QR code, 'intent' for UPI ID
            vpa: '', // Leave empty for QR code
            merchant_id: process.env.REACT_APP_RAZORPAY_KEY_ID,
            merchant_name: 'Ecom Buddha',
            merchant_url: window.location.origin
        },
        // Wallet configuration
        wallet: {
            phonepe: true,
            googlepay: true,
            paytm: true,
            airtelmoney: true,
            mobikwik: true,
            olamoney: true
        },
        modal: {
            ondismiss: function() {
                onError('Payment cancelled by user');
            }
        }
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
};

// Get user balance
export const getUserBalance = async (token) => {
    const response = await fetch(`${API_URL}/payment_integration/user-balance`, {
        method: 'GET',
        headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        }
    });
    return handleResponse(response);
};

